import '@/plugins/passiveEvents';
import '@fontsource/roboto/100.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/roboto/900.css';
import '@fontsource/cairo/400.css';
import '@fontsource/cairo/700.css';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import 'material-icons/iconfont/material-icons.css';
import 'font-awesome/css/font-awesome.css';
import Vue from "vue";
import '@/plugins/vuetify'
import App from "@/App.vue";
import router from "@/router";
import store from "@/store";
import "@/components/monaco/PermissionUtils";
import 'roboto-fontface/css/roboto/roboto-fontface.css';
import "mapbox-gl/dist/mapbox-gl.css";
import "@/assets/rtl-utils.css";
import axios from 'axios';
import api from '@/constants/api';
import moment from 'moment';
import titleMixin from '@/mixins/titleMixin';
import { ChartPlugin } from "@syncfusion/ej2-vue-charts";
import * as VueGoogleMaps from 'vue2-google-maps'
import VuejsDialog from 'vuejs-dialog';
import 'vuejs-dialog/dist/vuejs-dialog.min.css';
import Dialova from '@/plugins/dialova.js';
import VueTippy, { TippyComponent } from "vue-tippy";
import VueMask from "v-mask";
import './registerServiceWorker';
import formatDateMixin from "@/mixins/formatDate";
import { formatCompanyTime, companyFromNow } from "@/util/companyTime";
import "@/mixins/money";
import i18n, { applyLocaleSettings } from "@/i18n";

const isLocalAdminApi = /:8000$/.test(api.MAIN_URL);

function getRequestPayload(config) {
  if (!config || !config.data) return {};
  if (config.data instanceof FormData) {
    const payload = {};
    config.data.forEach((value, key) => {
      if (key === "_method") return;
      if (payload[key] !== undefined) {
        payload[key] = Array.isArray(payload[key])
          ? [...payload[key], value]
          : [payload[key], value];
      } else {
        payload[key] = value;
      }
    });
    return payload;
  }
  return config.data;
}

function getRelativeAdminUrl(config) {
  const url = (config && config.url ? config.url : "").replace(/^\/+/, "");
  return url.replace(/^https?:\/\/[^/]+\/api\/user\/?/i, "");
}

function getSearchParam(config, key) {
  if (!config) return null;
  if (config.params instanceof URLSearchParams) {
    return config.params.get(key);
  }
  if (config.params && typeof config.params.get === "function") {
    return config.params.get(key);
  }
  if (config.params && Object.prototype.hasOwnProperty.call(config.params, key)) {
    return config.params[key];
  }

  const relativeUrl = getRelativeAdminUrl(config);
  const queryIndex = relativeUrl.indexOf("?");
  if (queryIndex === -1) return null;
  return new URLSearchParams(relativeUrl.slice(queryIndex + 1)).get(key);
}

function getCollectionResponse(config, items = []) {
  const page = Number(getSearchParam(config, "page") || 1);
  const perPage = Number(getSearchParam(config, "perPage") || 10);

  return {
    message: "success",
    data: {
      current_page: page,
      data: items,
      last_page: 1,
      per_page: perPage,
      total: items.length,
    },
  };
}

function getActualMethod(config) {
  const method = ((config && config.method) || "get").toLowerCase();
  if (method !== "post") return method;
  const payload = getRequestPayload(config);
  return String(payload._method || method).toLowerCase();
}

function getErrorMessage(error) {
  if (!error || !error.response || !error.response.data) return "";
  const responseData = error.response.data;
  if (typeof responseData === "string") return responseData;
  return responseData.message || "";
}

function isMissingLocalApiError(error) {
  const status = error && error.response ? error.response.status : null;
  const message = String(getErrorMessage(error)).toLowerCase();

  if (status === 404 || status === 405) {
    return true;
  }

  if (status === 400) {
    return (
      message.includes("could not be found") ||
      message.includes("method is not supported")
    );
  }

  return false;
}

function shouldUseLocalFallback(config) {
  const relativeUrl = getRelativeAdminUrl(config).split("?")[0];

  if (relativeUrl === "permission") {
    return true;
  }

  if (["voucher"].includes(relativeUrl)) {
    return true;
  }

  if (/^user\/\d+\/(stats|meta)$/.test(relativeUrl) || relativeUrl === "user/stats") {
    return true;
  }

  return false;
}

function buildLocalFallbackPayload(config) {
  const relativeUrl = getRelativeAdminUrl(config).split("?")[0];
  const actualMethod = getActualMethod(config);
  const payload = getRequestPayload(config);
  const lastSegment = relativeUrl.split("/").filter(Boolean).pop() || "";
  const numericId = Number(lastSegment);
  const resourceId = Number.isNaN(numericId) ? lastSegment : numericId;

  if (actualMethod === "get") {
    if (relativeUrl === "permission") {
      return { message: "success", data: [] };
    }
    if (["captain_cancellation_reason", "cancellation-reason"].includes(relativeUrl)) {
      return { message: "success", data: [] };
    }
    if (["voucher"].includes(relativeUrl)) {
      return getCollectionResponse(config, []);
    }
    if (/^user\/\d+\/(stats|meta)$/.test(relativeUrl) || relativeUrl === "user/stats") {
      return { message: "success", data: {} };
    }
    if (/\/\d+$/.test(relativeUrl)) {
      return { message: "success", data: { id: resourceId } };
    }
    return getCollectionResponse(config, []);
  }

  if (actualMethod === "delete") {
    return { message: "success", data: { id: resourceId, deleted: true } };
  }

  return {
    message: "success",
    data: {
      id: typeof resourceId === "number" ? resourceId : Date.now(),
      ...payload,
    },
  };
}

Vue.use(VueGoogleMaps, {
    load: {
        key: 'AIzaSyAvWQlGwdRe7kdMxM9uHuEjjSkYxD1YmWs',
        libraries: 'places',
    },
});

Vue.use(Dialova);
Vue.use(VueMask);
Vue.component("tippy", TippyComponent);
Vue.config.productionTip = false;
axios.defaults.timeout = 1000 * 300;
axios.defaults.baseURL = api.MAIN_URL + '/api/user/';

if (isLocalAdminApi) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const relativeUrl = getRelativeAdminUrl(error ? error.config : null);

      if (isMissingLocalApiError(error) && error && error.config && shouldUseLocalFallback(error.config)) {
        console.warn(`Local API fallback used for ${relativeUrl || "<root>"}`);
        return Promise.resolve({
          data: buildLocalFallbackPayload(error.config),
          status: 200,
          statusText: "OK",
          headers: {},
          config: error.config,
        });
      }

      return Promise.reject(error);
    }
  );
}

// QA V4 W2 (#22): global 401 handler. The admin Passport token expires after
// ~8h; without this, an expired token mid-session left the SPA silently broken
// (every request 401s but the UI never reacts). Mirrors the business dashboard
// (src/main.ts). Only act when a user is actually in the store, so a failed
// login (401 on /login) does not trigger a redirect loop on the login page.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error &&
      error.response &&
      error.response.status === 401 &&
      store.state.user
    ) {
      store.commit("LOGOUT");
      if (router.currentRoute.name !== "Login") {
        router.push({ name: "Login" }).catch(() => {});
      }
    }
    return Promise.reject(error);
  }
);

// const circularStringify = function (v) {
//   const cache = new Set();
//   return JSON.stringify(v, function (key, value) {
//     if (typeof value === 'object' && value !== null) {
//       if (cache.has(value)) {
//         // Circular reference found, discard key
//         return;
//       }
//       // Store value in our set
//       cache.add(value);
//     }
//     return value;
// 	}, 2);
// };

// const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

Vue.prototype.axios = axios;
Vue.prototype.api = api;
Vue.prototype.moment = moment;
// Final-version doc 2026-10-05: templates print server timestamps on the
// company clock (Damascus, fixed UTC+3) through these, never the PC's zone.
Vue.prototype.$companyTime = formatCompanyTime;
Vue.prototype.$companyFromNow = companyFromNow;
// window.circularStringify = circularStringify;
window.moment = moment;
// window.clamp = clamp;
Vue.use(ChartPlugin);
Vue.use(VuejsDialog);
Vue.mixin(titleMixin);
Vue.mixin(formatDateMixin);

applyLocaleSettings(store.getters.selectedLanguage, Vue.prototype.$vuetify);

new Vue({
  i18n,
  router,
  store,
  render: h => h(App)
}).$mount('#app');
